import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Table } from '../ui/Table';
import { DownloadIcon, CopyIcon, FilterIcon, ExternalLinkIcon } from 'lucide-react';
interface AuditSession {
  id: string;
  serverId: string;
  serverName: string;
  startTime: string;
  endTime: string;
  clientType: string;
  allowedCount: number;
  deniedCount: number;
  blockedEgress: number;
  primaryDenialReason: string | null;
}
const mockSessions: AuditSession[] = [{
  id: 'ses_a1b2c3d4',
  serverId: 'srv_8x7k2m9p',
  serverName: 'Production API Gateway',
  startTime: '2024-01-15 14:32:00',
  endTime: '2024-01-15 14:45:23',
  clientType: 'Claude Desktop',
  allowedCount: 47,
  deniedCount: 3,
  blockedEgress: 1,
  primaryDenialReason: 'Network egress blocked'
}, {
  id: 'ses_e5f6g7h8',
  serverId: 'srv_8x7k2m9p',
  serverName: 'Production API Gateway',
  startTime: '2024-01-15 13:15:00',
  endTime: '2024-01-15 13:28:45',
  clientType: 'MCP Inspector',
  allowedCount: 23,
  deniedCount: 0,
  blockedEgress: 0,
  primaryDenialReason: null
}, {
  id: 'ses_i9j0k1l2',
  serverId: 'srv_3n5j8w2q',
  serverName: 'Data Processing Pipeline',
  startTime: '2024-01-15 12:00:00',
  endTime: '2024-01-15 12:42:18',
  clientType: 'Python SDK',
  allowedCount: 156,
  deniedCount: 12,
  blockedEgress: 5,
  primaryDenialReason: 'Tool not in allowlist'
}, {
  id: 'ses_m3n4o5p6',
  serverId: 'srv_9m4k1p6r',
  serverName: 'Analytics Service',
  startTime: '2024-01-15 10:30:00',
  endTime: '2024-01-15 11:15:33',
  clientType: 'Claude Desktop',
  allowedCount: 89,
  deniedCount: 47,
  blockedEgress: 23,
  primaryDenialReason: 'Network egress blocked'
}];
const columns = [{
  key: 'serverName',
  header: 'Server',
  render: (row: AuditSession) => <div data-id="element-220">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-id="element-221">
          {row.serverName}
        </span>
        <div className="text-xs font-mono text-slate-500 dark:text-slate-400" data-id="element-222">
          {row.serverId}
        </div>
      </div>
}, {
  key: 'startTime',
  header: 'Time Range',
  render: (row: AuditSession) => <div className="text-xs" data-id="element-223">
        <div className="text-slate-900 dark:text-slate-100" data-id="element-224">
          {row.startTime}
        </div>
        <div className="text-slate-500 dark:text-slate-400" data-id="element-225">
          → {row.endTime}
        </div>
      </div>
}, {
  key: 'clientType',
  header: 'Client',
  render: (row: AuditSession) => <Badge variant="neutral" data-id="element-226">{row.clientType}</Badge>
}, {
  key: 'decisions',
  header: 'Decisions',
  render: (row: AuditSession) => <div className="flex items-center gap-3 text-xs" data-id="element-227">
        <span className="text-green-600 dark:text-green-400 font-medium" data-id="element-228">
          {row.allowedCount} allowed
        </span>
        {row.deniedCount > 0 && <span className="text-red-600 dark:text-red-400 font-medium" data-id="element-229">
            {row.deniedCount} denied
          </span>}
      </div>
}, {
  key: 'blockedEgress',
  header: 'Blocked Egress',
  render: (row: AuditSession) => row.blockedEgress > 0 ? <Badge variant="denied" data-id="element-230">{row.blockedEgress}</Badge> : <span className="text-xs text-slate-400 dark:text-slate-500" data-id="element-231">—</span>
}, {
  key: 'primaryDenialReason',
  header: 'Primary Denial',
  render: (row: AuditSession) => row.primaryDenialReason ? <span className="text-xs text-slate-700 dark:text-slate-300" data-id="element-232">
          {row.primaryDenialReason}
        </span> : <span className="text-xs text-slate-400 dark:text-slate-500" data-id="element-233">—</span>
}, {
  key: 'actions',
  header: '',
  width: '80px',
  render: (row: AuditSession) => <Link to={`/audit/${row.id}`} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1" data-id="element-234">
        View
        <ExternalLinkIcon className="h-3 w-3" data-id="element-235" />
      </Link>
}];
export function AuditInbox() {
  const [serverFilter, setServerFilter] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  return <div data-id="element-236">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-237">
        <div data-id="element-238">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100" data-id="element-239">
            Audit Inbox
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5" data-id="element-240">
            Review security decisions and blocked behavior
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-241">
          <Button variant="secondary" size="sm" data-id="element-242">
            <CopyIcon className="h-4 w-4 mr-1.5" data-id="element-243" />
            Copy API Request
          </Button>
          <Button variant="secondary" size="sm" data-id="element-244">
            <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-245" />
            Download JSONL
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6" data-id="element-246">
        <div className="flex items-end gap-4" data-id="element-247">
          <div className="flex-1" data-id="element-248">
            <Input label="Search" placeholder="Search by session ID, method, or tool..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-249" />
          </div>
          <div className="w-48" data-id="element-250">
            <Select label="Server" options={[{
            value: '',
            label: 'All servers'
          }, {
            value: 'srv_8x7k2m9p',
            label: 'Production API Gateway'
          }, {
            value: 'srv_3n5j8w2q',
            label: 'Data Processing Pipeline'
          }, {
            value: 'srv_9m4k1p6r',
            label: 'Analytics Service'
          }]} value={serverFilter} onChange={e => setServerFilter(e.target.value)} data-id="element-251" />
          </div>
          <div className="w-36" data-id="element-252">
            <Select label="Decision" options={[{
            value: '',
            label: 'All'
          }, {
            value: 'allowed',
            label: 'Allowed'
          }, {
            value: 'denied',
            label: 'Denied'
          }]} value={decisionFilter} onChange={e => setDecisionFilter(e.target.value)} data-id="element-253" />
          </div>
          <Button variant="secondary" data-id="element-254">
            <FilterIcon className="h-4 w-4 mr-1.5" data-id="element-255" />
            More Filters
          </Button>
        </div>
      </Card>

      {/* Sessions Table */}
      <Card padding="none" data-id="element-256">
        <Table columns={columns} data={mockSessions} data-id="element-257" />
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-258">
        <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-259">
          Showing 1-4 of 156 sessions
        </p>
        <div className="flex items-center gap-2" data-id="element-260">
          <Button variant="secondary" size="sm" disabled data-id="element-261">
            Previous
          </Button>
          <Button variant="secondary" size="sm" data-id="element-262">
            Next
          </Button>
        </div>
      </div>
    </div>;
}